import { getDashboardUsersControllerListQueryKey } from "@/api/__generated__/dashboard-users/dashboard-users";
import { MessageKey } from "@/i18n";
import {
  AdditionalUserRole,
  ConnectionField,
  Gender,
  ThirdPartyLicenseType,
  UserExportBaseColumn,
  UserExportColumn,
  UserExportColumnValues,
  UserRole,
  UsersTableColumn,
  UserSignupSource,
  USState,
} from "@/types";

export const USERS_INVALIDATE_KEYS = [getDashboardUsersControllerListQueryKey()];

export const AGE_TYPE_VALUES = ["actualAge", "birthYear"] as const;
export type AgeType = (typeof AGE_TYPE_VALUES)[number];

/** Platform that uses CTFID (`user_national_id_2`). */
export const CTF_PLATFORM_ID = 25;

export const AGE_TYPE_LABELS: { [key in AgeType]: MessageKey } = {
  actualAge: "user.filters.ageType.actualAge" as MessageKey,
  birthYear: "user.filters.ageType.birthYear" as MessageKey,
};

export const USER_ROLES_LABELS: { [key in UserRole]: MessageKey } = {
  federation: "user.enums.role.federation",
  superAdmin: "user.enums.role.superAdmin",
  clubAdministrator: "user.enums.role.clubAdministrator",
  coach: "user.enums.role.coach",
  admin: "user.enums.role.admin",
  athlete: "user.enums.role.athlete",
  user: "user.enums.role.user",
  fan: "user.enums.role.fan",
  member: "user.enums.role.member",
};

export const ADDITIONAL_USER_ROLE_LABELS: {
  [key in AdditionalUserRole]: MessageKey;
} = {
  athlete: "user.enums.additionalRole.athlete",
  federationMember: "user.enums.additionalRole.federationMember",
  clubOwner: "user.enums.additionalRole.clubOwner",
  clubManager: "user.enums.additionalRole.clubManager",
  referee: "user.enums.additionalRole.referee",
  tournamentOrganizer: "user.enums.additionalRole.tournamentOrganizer",
  sportFan: "user.enums.additionalRole.sportFan",
  other: "user.enums.additionalRole.other",
};

export const USERS_TABLE_COLUMN_LABELS: { [key in UsersTableColumn]: MessageKey } = {
  number: "general.columns.number",
  ctfId: "user.columns.ctfId",
  user: "user.columns.user",
  contacts: "general.columns.contactDetails",
  age: "general.columns.age",
  roles: "user.columns.roles",
  sports: "user.columns.sports",
  clubs: "general.columns.club",
  entries: "user.columns.entries",
  transactions: "user.columns.transactions",
  thirdParties: "user.columns.connections",
  platforms: "user.columns.platforms",
  organizations: "user.columns.organizations",
  joined: "user.columns.joined",
};

/**
 * Fixed users-export columns, always offered. Player-connection columns are
 * appended per filter scope by `UsersExportModal` (see `useUserConnectionColumns`).
 */
export const USER_EXPORT_COLUMNS: readonly UserExportBaseColumn[] = UserExportColumnValues;

export const GENDER_LABELS: { [key in Gender]: MessageKey } = {
  male: "user.enums.gender.male",
  female: "user.enums.gender.female",
};

/** USPS state names are proper names, so they deliberately do not use locale keys. */
export const US_STATE_NAMES: { [key in USState]: string } = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

export const USER_SIGNUP_SOURCE_LABELS: { [key in UserSignupSource]: MessageKey } = {
  web: "user.enums.signupSource.web",
  mobile: "user.enums.signupSource.mobile",
  admin: "user.enums.signupSource.admin",
  manager: "user.enums.signupSource.manager",
};
export const THIRD_PARTY_LICENSE_TYPE_LABELS: {
  [key in ThirdPartyLicenseType]: string;
} = {
  dupr: "DUPR",
  wpr: "WPR",
  ipin: "IPIN",
  itfPoints: "ITF Points",
  fipPoints: "FIP Points",
  fideId: "FIDEID",
  fivb: "FIVB",
};

export const THIRD_PARTY_LICENSE_ID_TO_TYPE: {
  [key in number]: ThirdPartyLicenseType;
} = {
  1: "ipin",
  4: "dupr",
  5: "fideId",
  14: "wpr",
  15: "itfPoints",
  16: "fivb",
  21: "fipPoints",
};

export const USER_EXPORT_BASE_HEADERS = [
  "Name",
  "Surname",
  "Email",
  "Phone number",
  "Date of birth",
  "Country",
  "Role",
];

export const THIRD_PARTY_ID_TO_USER_FIELD: Record<number, string> = {
  1: "ipinId",
  2: "atpId",
  3: "wtaId",
  4: "duprId",
  5: "fideId",
  8: "teU12Id",
  9: "otherPoints",
  10: "bonusU12",
  11: "bonusU14",
  12: "bonusU16",
  13: "bonusPoints",
  14: "wprPlayerId",
  15: "itf_points",
  16: "fivb",
  18: "wprUserId",
  20: "uciId",
  21: "fipPoints",
};

export const THIRD_PARTY_ID_TO_HEADER: Record<number, string> = {
  1: "IPIN",
  4: "DUPRID",
  14: "WPR",
  13: "Bonus points",
  10: "Bonus U12",
  11: "Bonus U14",
  12: "Bonus U16",
  9: "Other points",
  8: "TE U12",
  5: "FIDEID",
  3: "WTA",
  2: "ATP",
  16: "FIVB",
  15: "ITF points",
  18: "WPR | USPA",
  20: "UCI ID",
  21: "FIP points",
};

/**
 * Label map for the player-connection columns (layer 2 of the enum-label
 * pattern). Consumed via `useGetConnectionFieldLabel`.
 */
export const CONNECTION_FIELD_LABELS: { [key in ConnectionField]: MessageKey } = {
  fivb: "user.columns.connection.fivb",
  duprId: "user.columns.connection.duprId",
  ipinId: "user.columns.connection.ipinId",
  teId: "user.columns.connection.teId",
  atpId: "user.columns.connection.atpId",
  wtaId: "user.columns.connection.wtaId",
  fideId: "user.columns.connection.fideId",
  pdlId: "user.columns.connection.pdlId",
  uciId: "user.columns.connection.uciId",
  ltsU10Id: "user.columns.connection.ltsU10Id",
  teU12Id: "user.columns.connection.teU12Id",
  wprPlayerId: "user.columns.connection.wprPlayerId",
  itf_points: "user.columns.connection.itfPoints",
  cpcPoints: "user.columns.connection.cpcPoints",
  maxwillPoints: "user.columns.connection.maxwillPoints",
  fipPoints: "user.columns.connection.fipPoints",
  otherPoints: "user.columns.connection.otherPoints",
};

export const USER_EXPORT_COLUMN_LABELS: { [key in UserExportColumn]: MessageKey } = {
  name: "general.columns.name",
  surname: "general.columns.surname",
  email: "general.columns.email",
  phone: "user.export.columns.phone",
  birthDate: "user.export.columns.birthDate",
  nation: "user.export.columns.nation",
  role: "user.columns.role",
  gender: "general.columns.gender",
  city: "general.columns.city",
  country: "general.columns.country",
  club: "general.columns.club",
  ...CONNECTION_FIELD_LABELS,
};

/**
 * Registry describing every known player-connection column. Keyed by the User
 * column `field` name that a platform's `ThirdPartyLicense.field` points at.
 * `editable: false` marks SSO-managed fields (DUPR) that render read-only.
 * A `field` returned by a platform that is not in this registry is ignored.
 */
export const CONNECTION_FIELD_REGISTRY: {
  [key in ConnectionField]: { columnId: string; editable: boolean };
} = {
  fivb: { columnId: "connection-fivb", editable: true },
  duprId: { columnId: "connection-duprId", editable: false },
  ipinId: { columnId: "connection-ipinId", editable: true },
  teId: { columnId: "connection-teId", editable: true },
  atpId: { columnId: "connection-atpId", editable: true },
  wtaId: { columnId: "connection-wtaId", editable: true },
  fideId: { columnId: "connection-fideId", editable: true },
  pdlId: { columnId: "connection-pdlId", editable: true },
  uciId: { columnId: "connection-uciId", editable: true },
  ltsU10Id: { columnId: "connection-ltsU10Id", editable: true },
  teU12Id: { columnId: "connection-teU12Id", editable: true },
  wprPlayerId: { columnId: "connection-wprPlayerId", editable: true },
  itf_points: { columnId: "connection-itf_points", editable: true },
  cpcPoints: { columnId: "connection-cpcPoints", editable: true },
  maxwillPoints: { columnId: "connection-maxwillPoints", editable: true },
  fipPoints: { columnId: "connection-fipPoints", editable: true },
  otherPoints: { columnId: "connection-otherPoints", editable: true },
};

const CONNECTION_FIELD_SET = new Set<string>(Object.keys(CONNECTION_FIELD_REGISTRY));

/**
 * Legacy `ThirdPartyLicense.field` values that don't match the canonical `User`
 * column name.
 *
 * `wprId` is ASSUMED to mean `wprPlayerId`, matching the team-members CSV import
 * (`row.wprId` → `wprPlayerId`). This is unverified: `User` has two distinct WPR
 * columns — `wprUserId` (the WPR account) and `wprPlayerId` (the WPR lookup key)
 * — and the `syncWPRData` mutation reads its own `wprId` argument as `wprUserId`.
 * If a license row with `field = "wprId"` actually denotes the account id, this
 * alias routes it into the wrong column. Confirm against production before
 * relying on it:
 *   SELECT id, title, field FROM third_party_license WHERE field ILIKE '%wpr%';
 */
const LICENSE_FIELD_ALIASES: Record<string, ConnectionField> = {
  wprId: "wprPlayerId",
};

/**
 * Normalises a platform-license `field` string to a known ConnectionField,
 * applying legacy aliases (e.g. `wprId` → `wprPlayerId`). Returns null when the
 * field maps to no known connection column.
 */
export const resolveConnectionField = (
  field: string | null | undefined
): ConnectionField | null => {
  if (field == null) return null;
  const normalized = LICENSE_FIELD_ALIASES[field] ?? field;
  return CONNECTION_FIELD_SET.has(normalized) ? (normalized as ConnectionField) : null;
};
